// @ts-strict-ignore
import * as React from 'react';
import { AppComponentProps, ExtendedStatsByType, MetricsUsageOnAutomationsMap } from '../../typescriptHelpers';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import Popover from 'antd/lib/popover';
import Skeleton from 'antd/lib/skeleton';
import Space from 'antd/lib/space';
import Table from 'antd/lib/table';
import Typography from 'antd/lib/typography';
import { AppSettingsSection } from './AppSettingsSection';
import { Link } from 'react-router-dom';
import { createAppSettingsRouteWithSettingType } from '../../helpers/routeHelpers';
import { LinkOutlined, SettingOutlined, EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
    AllMonitoringHealthMetricCategories,
    AppMonitoringHealthMetric,
    AppMonitoringHealthMetricCategory,
    AppStoreType,
    AutomationHaltUnstableRelease,
    AutomationPromoteStablePhasedRelease,
    AutomationType,
    IntegrationCapability,
    IntegrationType,
    ObservabilityAndAnalyticsMetricType,
    SettingType,
    StabilityMetricType,
    SubscriptionFeatureType,
    UserRatingsMetricType,
} from '../../types';
import {
    allSupportedIntegrationTypesForCapability,
    requiredCapabilityForMonitoringMetricCategory,
    getIntegrationTypeForAppStoreType,
    getIsOTAPlatform,
    extendedStatisticTypeForMonitoringMetric,
    extendedStatTypeForReleaseStatistic,
    unitForMonitoringMetric,
} from '../../helpers';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import {
    selectAppSettings,
    selectLatestVersion,
    selectRelease,
    selectConnectedCapabilitiesForApp,
    selectAppStoreType,
    selectIsRunwayOrg,
    selectAppPlatform,
    selectAppIntegrations,
    selectReleaseStatsHistory,
    selectAppSettingAutomations,
} from '../../selectors/selectors';
import { iconAndAltForIntegrationType } from '../../helpers/integrationHelpers';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { ColumnsType } from 'antd/lib/table';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { AddEditMonitoringMetricModal, mapThresholdToFormValue } from './AddEditMonitoringMetricModal';
import { DeleteMonitoringMetricRequest, tryUpdateAppSettings } from '../../actions/appSettings';
import { AppSettingsUpdatable } from '../../actions/updatable';
import { globalMessage } from '../../utils/globalMessage';
import { FetchableEntityType } from '../../entityType';
import { selectAveragedReleaseStatistics } from '../../selectors/memoizedSelectors';
import { tryFetchEntity } from '../../actions/entities';
import { compact, forIn, identity } from 'lodash';
import _ from 'lodash';
import {
    displayFormattedMonitoringMetricValueWithSign,
    displayObservabilityAndAnalyticsMetricsTypeAsAverage,
    displaySectionTitleForMonitoringCategory,
    displayStoreNameForAppStoreType,
    displayTextForMonitoringMetric,
} from '../../helpers/displayHelpers';
import { generateAutomationThunderboltIcon, MonitoringMetricDescriptionIcon } from '../monitoring/monitoringHelpers';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import IntegrationIcon from '../icons/IntegrationIcon';
import { ThresholdDescription, isDeltaMetric } from './MonitoringMetricFormFields';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

interface MonitoringProps extends AppComponentProps {
    refProp?: React.MutableRefObject<HTMLDivElement>;
}

interface ConnectIntegrationButtonProps extends AppComponentProps {
    integrationCapability: IntegrationCapability;
    appStoreType: AppStoreType;
}

const buttonTitleForIntegrationCapability = (
    integrationCapability: IntegrationCapability,
    appStoreType: AppStoreType,
) => {
    switch (integrationCapability) {
        case IntegrationCapability.StabilityMonitoring:
            return 'Connect a stability monitoring integration';
        case IntegrationCapability.ObservabilityAndAnalytics:
            return 'Connect an observability & analytics integration';
        case IntegrationCapability.AppStore:
            return `Connect to the ${displayStoreNameForAppStoreType(appStoreType)}`;
    }

    return null;
};

const ConnectIntegrationButton: React.FC<ConnectIntegrationButtonProps> = ({
    integrationCapability,
    orgId,
    appId,
    appStoreType,
}) => {
    const buttonTitle = buttonTitleForIntegrationCapability(integrationCapability, appStoreType);
    const isRunwayOrg = useSelector((state: RootState) => selectIsRunwayOrg(state));

    const possibleIntegrations =
        integrationCapability !== IntegrationCapability.AppStore ?
            allSupportedIntegrationTypesForCapability(integrationCapability, isRunwayOrg, orgId)
        :   [getIntegrationTypeForAppStoreType(appStoreType)];

    return (
        <Space size={22}>
            <Link
                to={{
                    pathname: createAppSettingsRouteWithSettingType(orgId, appId, SettingType.Integrations),
                    state: {
                        scrollTo: integrationCapability,
                    },
                }}
            >
                {buttonTitle && (
                    <Button icon={<LinkOutlined style={{ color: runwayColors.primary }} />}>{buttonTitle}</Button>
                )}
            </Link>
            <Space size={14}>
                {possibleIntegrations.map((integration) => {
                    const [icon, alt] = iconAndAltForIntegrationType(integration);

                    return <img key={integration} src={icon} alt={alt} width={30} height={30} />;
                })}
            </Space>
        </Space>
    );
};

const sectionTitleForIntegration = (defaultText: string, integrationIds: IntegrationType[]) => (
    <Space size={12} align="center">
        <div>{defaultText}</div>
        {integrationIds.map((integrationId) => {
            const [icon, alt] = iconAndAltForIntegrationType(integrationId);
            return icon && <img key={integrationId} src={icon} alt={alt} width={16} height={16} />;
        })}
    </Space>
);

export const FormattedMetricDescription: React.FC<{ metric: AppMonitoringHealthMetric }> = ({ metric }) => {
    if (metric.thresholds.length === 0) {
        return (
            <Typography.Text
                style={{
                    color: antGrey.grey7,
                }}
            >
                Not set
            </Typography.Text>
        );
    }

    const threshold = metric.thresholds[0];

    return (
        <ThresholdDescription
            metricType={metric.type}
            threshold={mapThresholdToFormValue(threshold)}
            unit={unitForMonitoringMetric(metric)}
        />
    );
};

const ConfirmRemoveMonitoringMetricModal: React.FC<{
    autoHaltAutomation: AutomationHaltUnstableRelease;
    autoPromoteAutomation: AutomationPromoteStablePhasedRelease;
    onRemoved: () => void;
    onCancel: () => void;
    appId: string;
    orgId: string;
    metric?: AppMonitoringHealthMetric;
}> = ({ onRemoved, onCancel, orgId, appId, metric, autoHaltAutomation, autoPromoteAutomation }) => {
    const dispatch = useDispatch();

    const removeMonitoringMetric = (metricId: string) => {
        const deleteMonitoringMetricRequest: DeleteMonitoringMetricRequest = {
            orgId,
            appId,
            metricId,
        };

        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.DeleteMonitoringMetric, deleteMonitoringMetricRequest));
    };

    const deleteMonitoringMetricIsLoading = useIsLoading(AppSettingsUpdatable.DeleteMonitoringMetric);
    const deleteMonitoringMetricError = useError(AppSettingsUpdatable.DeleteMonitoringMetric);
    const deleteMonitoringSettingsSuccess = useUpdated(AppSettingsUpdatable.DeleteMonitoringMetric);

    React.useEffect(() => {
        if (deleteMonitoringMetricError) {
            globalMessage.error(deleteMonitoringMetricError);
        }
    }, [deleteMonitoringMetricError]);

    React.useEffect(() => {
        if (deleteMonitoringSettingsSuccess) {
            globalMessage.success('Updated monitoring settings!');
            onRemoved();
        }
    }, [deleteMonitoringSettingsSuccess]);

    const automationsUsingTheMetric = compact([autoHaltAutomation, autoPromoteAutomation]).filter(
        (automation) => metric && automation.data?.monitoringConditions?.includes(metric.id),
    );

    const displayText = metric ? displayTextForMonitoringMetric(metric, true) : '';

    let alert: React.ReactNode = null;

    if (automationsUsingTheMetric.length > 0) {
        const automationNames: React.ReactNode[] = [];

        automationsUsingTheMetric.forEach((automation, index) => {
            if (index > 0) {
                automationNames.push(index === automationsUsingTheMetric.length - 1 ? ' and ' : ', ');
            }

            automationNames.push(<strong>{automation.name}</strong>);
        });

        const automationsThatWouldBeDisabled = automationsUsingTheMetric.filter(
            (automation) => automation.data?.monitoringConditions?.length === 1,
        );

        const automationNamesThatWouldBeDisabled: React.ReactNode[] = [];
        automationsThatWouldBeDisabled.forEach((automation, index) => {
            if (index > 0) {
                automationNames.push(index === automationsThatWouldBeDisabled.length - 1 ? ' and ' : ', ');
            }
            automationNamesThatWouldBeDisabled.push(<strong>{automation.name}</strong>);
        });

        alert = (
            <>
                <br />
                <br />
                <Typography.Text>
                    This health metric is being used by the {automationNames.map(identity)}{' '}
                    {automationsUsingTheMetric.length > 1 ? 'automations' : 'automation'}.
                </Typography.Text>
                {automationNamesThatWouldBeDisabled.length > 0 && (
                    <Alert
                        style={{ marginTop: 16 }}
                        message={
                            <Typography.Text>
                                Removing this health metric will disable the{' '}
                                {automationNamesThatWouldBeDisabled.map(identity)}{' '}
                                {automationNamesThatWouldBeDisabled.length > 1 ? 'automations' : 'automation'}.
                            </Typography.Text>
                        }
                        type="warning"
                        showIcon
                    />
                )}
            </>
        );
    }

    return (
        <Modal
            open={!!metric}
            title="Remove health metric"
            okText="Remove"
            onCancel={onCancel}
            onOk={() => removeMonitoringMetric(metric?.id)}
            okButtonProps={{ loading: deleteMonitoringMetricIsLoading, danger: true }}
            destroyOnClose
        >
            <Typography.Text>
                Are you sure you want to remove the <strong>{displayText}</strong> metric?
            </Typography.Text>
            {alert}
        </Modal>
    );
};
const generateMetricUsageOnAutomationsMap = (
    autoHaltAutomation: AutomationHaltUnstableRelease,
    autoPromoteAutomation: AutomationPromoteStablePhasedRelease,
): MetricsUsageOnAutomationsMap => {
    const finalMap: MetricsUsageOnAutomationsMap = {};
    const haltMonitoringConditions = autoHaltAutomation?.data?.monitoringConditions ?? [];

    if (haltMonitoringConditions) {
        compact(haltMonitoringConditions).forEach((metricId) => {
            if (finalMap[metricId]) {
                finalMap[metricId].isUsedForAutoHalt = true;
            } else {
                finalMap[metricId] = {
                    isUsedForAutoHalt: true,
                    isUsedForAutoAccelerate: false,
                };
            }
        });
    }

    const promoteMonitoringConditions = autoPromoteAutomation?.data?.monitoringConditions ?? [];
    if (promoteMonitoringConditions) {
        compact(promoteMonitoringConditions).forEach((metricId) => {
            if (finalMap[metricId]) {
                finalMap[metricId].isUsedForAutoAccelerate = true;
            } else {
                finalMap[metricId] = {
                    isUsedForAutoHalt: false,
                    isUsedForAutoAccelerate: true,
                };
            }
        });
    }

    return finalMap;
};

export const Monitoring: React.FC<MonitoringProps> = (props: MonitoringProps) => {
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const platform = useSelector((state: RootState) => selectAppPlatform(state, props.appId));

    const dispatch = useDispatch();

    const [selectedMetric, setSelectedMetric] = React.useState<AppMonitoringHealthMetric>(null);
    const [metricCategory, setMetricCategory] = React.useState<AppMonitoringHealthMetricCategory>(null);

    const monitoringSettings = useSelector(
        (state: RootState) => selectAppSettings(state, props.appId).monitoringSettings,
    );

    const connectedIntegrationCapabilities = useSelector((state: RootState) =>
        selectConnectedCapabilitiesForApp(state, props.appId),
    );

    const integrations = useSelector((state: RootState) => selectAppIntegrations(state, props.appId));

    // TODO: #WORKFLOWS# in the future there might be multiple automations of the same type
    const autoHaltAutomation = useSelector((state: RootState) => {
        const automations = selectAppSettingAutomations(state, props.appId);
        return automations.find((automation) => automation.type === AutomationType.AutoHaltUnstablePhasedReleases);
    }) as AutomationHaltUnstableRelease;

    // TODO: #WORKFLOWS# in the future there might be multiple automations of the same type
    const autoPromoteAutomation = useSelector((state: RootState) => {
        const automations = selectAppSettingAutomations(state, props.appId);
        return automations.find((automation) => automation.type === AutomationType.AutoPromoteStablePhasedReleases);
    }) as AutomationPromoteStablePhasedRelease;

    const metricUsageOnAutomationsMap = generateMetricUsageOnAutomationsMap(autoHaltAutomation, autoPromoteAutomation);
    const isRunwayOrg = useSelector((state: RootState) => selectIsRunwayOrg(state));

    const latestCompletedVersion = useSelector((state: RootState) => selectLatestVersion(state, props.appId));

    const latestReleaseId = `${props.appId}:${latestCompletedVersion}`;

    const averagedReleasesCount = useSelector(
        (state: RootState) =>
            selectReleaseStatsHistory(state, latestReleaseId)?.filter((stat) => stat.completedAtOrReleasedAtTimestamp)
                ?.length ?? 0,
    );

    const isLoadingReleaseStatsHistory = useIsLoading(FetchableEntityType.ReleaseStats, latestReleaseId);
    const fetchReleaseStatsHistoryError = useError(FetchableEntityType.ReleaseStats, latestReleaseId);
    const currentReleaseStatsMap: ExtendedStatsByType = useSelector((state: RootState) => {
        const { currentReleaseStats } = selectAveragedReleaseStatistics(state, latestReleaseId);

        if (!currentReleaseStats) {
            return {};
        }

        return currentReleaseStats.reduce((acc, curr) => {
            const extendedType = extendedStatTypeForReleaseStatistic(curr);
            acc[extendedType] = curr;
            return acc;
        }, {});
    });

    const release = useSelector((state: RootState) => selectRelease(state, latestReleaseId));

    React.useEffect(() => {
        if (props.appId && latestCompletedVersion && release && !release.releaseStatsHistory) {
            dispatch(
                tryFetchEntity(
                    FetchableEntityType.ReleaseStats,
                    { orgId: props.orgId, appId: props.appId, releaseId: latestReleaseId },
                    latestReleaseId,
                ),
            );
        }
    }, [props.appId, latestCompletedVersion]);

    const availableMetricTypes = [
        StabilityMetricType.SessionStability,
        StabilityMetricType.SessionStabilityDeltaFromPreviousPercentage,
        StabilityMetricType.SessionStabilityDeltaFromPreviousValue,
        StabilityMetricType.UserStability,
        StabilityMetricType.UserStabilityDeltaFromPreviousPercentage,
        StabilityMetricType.UserStabilityDeltaFromPreviousValue,
        UserRatingsMetricType.AvgUserRating,
        UserRatingsMetricType.AvgUserRatingDeltaFromPreviousPercentage,
        UserRatingsMetricType.AvgUserRatingDeltaFromPreviousValue,
        ObservabilityAndAnalyticsMetricType.AveragePropertyValues,
        ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousPercentage,
        ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousValue,
        ObservabilityAndAnalyticsMetricType.EventsPerActiveUser,
        ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousPercentage,
        ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousValue,
        ObservabilityAndAnalyticsMetricType.EventsPerSession,
        ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousPercentage,
        ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousValue,
        ObservabilityAndAnalyticsMetricType.MedianPropertyValues,
        ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousPercentage,
        ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousValue,
        ObservabilityAndAnalyticsMetricType.P90PropertyValues,
        ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousPercentage,
        ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousValue,
        ObservabilityAndAnalyticsMetricType.P95PropertyValues,
        ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousPercentage,
        ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousValue,
        ObservabilityAndAnalyticsMetricType.P99PropertyValues,
        ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousPercentage,
        ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousValue,
        ObservabilityAndAnalyticsMetricType.GenericObaaValue,
        ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousPercentage,
        ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousValue,
        ObservabilityAndAnalyticsMetricType.Composed,
        ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousPercentage,
        ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousValue,
    ];

    const metricsByCategory = _.groupBy(monitoringSettings.metrics, (metric) => metric.category);

    const [selectedOptionMetric, setSelectedOptionsMetric] = React.useState<AppMonitoringHealthMetric>(null);

    const [selectedRemoveMetric, setSelectedRemoveMetric] = React.useState<AppMonitoringHealthMetric>(null);

    const renderActions = (metric: AppMonitoringHealthMetric) => {
        return (
            <Popover
                overlayClassName="org-settings-popover" //same styles as org settings for consistency
                overlayStyle={{ paddingBottom: '0px' }}
                trigger="click"
                open={selectedOptionMetric?.id === metric?.id}
                placement="topRight"
                onOpenChange={(open) => {
                    if (open) {
                        setSelectedOptionsMetric(metric);
                    } else {
                        setSelectedOptionsMetric(null);
                    }
                }}
                content={
                    <div style={{ width: 210 }}>
                        <PermissionedActionButton
                            type="link"
                            icon={<EditOutlined style={{ color: antGrey.grey7 }} />}
                            onClick={() => {
                                setSelectedMetric(metric);
                                setMetricCategory(metric.category);
                                setSelectedOptionsMetric(null);
                            }}
                            style={{ display: 'flex', alignItems: 'center' }}
                            actionType={RunwayUserAction.UAUpdateAppSettings}
                        >
                            Edit health metric
                        </PermissionedActionButton>
                        <PermissionedActionButton
                            icon={<DeleteOutlined />}
                            onClick={() => {
                                setSelectedRemoveMetric(metric);
                                setSelectedOptionsMetric(null);
                            }}
                            actionType={RunwayUserAction.UAUpdateAppSettings}
                            type="link"
                            danger
                        >
                            Remove health metric
                        </PermissionedActionButton>
                    </div>
                }
                getPopupContainer={(trigger) => trigger.parentElement}
                destroyTooltipOnHide={true}
            >
                <SettingOutlined style={{ fontSize: '17px', paddingInlineEnd: '30px', color: antGrey.grey8 }} />
            </Popover>
        );
    };

    const renderMetric = (metric: AppMonitoringHealthMetric) => {
        const isUsedForAutoAccelerate = metricUsageOnAutomationsMap[metric.id]?.isUsedForAutoAccelerate ?? false;
        const isUsedForAutoHalt = metricUsageOnAutomationsMap[metric.id]?.isUsedForAutoHalt ?? false;
        const displayText = displayTextForMonitoringMetric(metric);

        return (
            <Space>
                <IntegrationIcon integrationId={metric.integrationId} size={24} />
                <Typography.Text>{displayText}</Typography.Text>
                {metric.description && <MonitoringMetricDescriptionIcon description={metric.description} />}
                {generateAutomationThunderboltIcon({
                    isUsedForAutoAccelerate,
                    isUsedForAutoHalt,
                    prefix: 'This rule is being used for the',
                })}
            </Space>
        );
    };

    const columnsForMetricCategory = (metricCategory: AppMonitoringHealthMetricCategory): ColumnsType => {
        return [
            {
                title: () => {
                    return (
                        <Typography.Text
                            style={{
                                color: antGrey.grey7,
                                fontSize: 12,
                            }}
                        >
                            Metric
                        </Typography.Text>
                    );
                },
                render: renderMetric,
            },
            ...(metricCategory === AppMonitoringHealthMetricCategory.ObservabilityAndAnalytics ?
                [
                    {
                        title: () => {
                            return (
                                <Typography.Text
                                    style={{
                                        color: antGrey.grey7,
                                        fontSize: 12,
                                    }}
                                >
                                    Metric type
                                </Typography.Text>
                            );
                        },
                        render: (metric: AppMonitoringHealthMetric) => {
                            return (
                                <Typography.Text
                                    style={{
                                        color: antGrey.grey8,
                                        fontSize: 14,
                                    }}
                                >
                                    {displayObservabilityAndAnalyticsMetricsTypeAsAverage(
                                        metric.type as ObservabilityAndAnalyticsMetricType,
                                        metric.integrationId,
                                        true,
                                    )}
                                </Typography.Text>
                            );
                        },
                    },
                ]
            :   []),
            {
                title: () => {
                    return (
                        <Typography.Text
                            style={{
                                color: antGrey.grey7,
                                fontSize: 12,
                            }}
                        >
                            Threshold definition
                        </Typography.Text>
                    );
                },
                render: (metric: AppMonitoringHealthMetric) => {
                    const statTypeForMetric = extendedStatisticTypeForMonitoringMetric(
                        metric.type,
                        metric.integrationId,
                        metric.eventName,
                    );
                    const currentAverage = currentReleaseStatsMap[statTypeForMetric]?.average;
                    const formattedAverage =
                        currentAverage ?
                            displayFormattedMonitoringMetricValueWithSign(
                                metric.type,
                                currentAverage,
                                unitForMonitoringMetric(metric),
                            )
                        :   null;

                    const isDelta = isDeltaMetric(metric.type);

                    return (
                        <Space direction="vertical" size={0}>
                            <FormattedMetricDescription metric={metric} />
                            <Typography.Text
                                style={{
                                    color: antGrey.grey7,
                                    fontSize: 12,
                                }}
                            >
                                {isLoadingReleaseStatsHistory ?
                                    <Space align="center">
                                        <Skeleton
                                            className="monitoring-settings-skeleton"
                                            active
                                            title={{
                                                width: 50,
                                            }}
                                            paragraph={false}
                                        />
                                    </Space>
                                :   <>
                                        {!isDelta && formattedAverage && averagedReleasesCount > 0 && (
                                            <span>
                                                Last {averagedReleasesCount} releases:{' '}
                                                <strong>{formattedAverage}</strong>
                                            </span>
                                        )}
                                        {fetchReleaseStatsHistoryError && (
                                            <span
                                                style={{
                                                    color: runwayColors.statusRed,
                                                }}
                                            >
                                                {fetchReleaseStatsHistoryError}
                                            </span>
                                        )}
                                    </>
                                }
                            </Typography.Text>
                        </Space>
                    );
                },
            },
            {
                title: '',
                align: 'right',
                render: renderActions,
            },
        ];
    };

    const allowedHealthMetricCategoriesForPlatform =
        getIsOTAPlatform(platform) ?
            [AppMonitoringHealthMetricCategory.ObservabilityAndAnalytics, AppMonitoringHealthMetricCategory.Stability]
        :   AllMonitoringHealthMetricCategories;

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }} ref={props.refProp}>
            <Space direction="vertical" size={40}>
                {allowedHealthMetricCategoriesForPlatform.map((metricCategory, index) => {
                    const sectionTitle = displaySectionTitleForMonitoringCategory(
                        metricCategory as AppMonitoringHealthMetricCategory,
                    );
                    const metrics = metricsByCategory[metricCategory] ?? [];
                    let sortedMetrics: AppMonitoringHealthMetric[] = [];

                    const groupedByIntegration = _.groupBy(metrics, (metric) => metric.integrationId);

                    forIn(groupedByIntegration, (metrics) => {
                        const sortedMetricsByIntegration = metrics;

                        sortedMetricsByIntegration.sort((a, b) => {
                            if (a.eventName && b.eventName) {
                                if (a.eventName === b.eventName) {
                                    return a.type < b.type ? -1 : 1;
                                }

                                return a.eventName < b.eventName ? -1 : 1;
                            } else {
                                return a.type < b.type ? -1 : 1;
                            }
                        });

                        sortedMetrics = [...sortedMetrics, ...sortedMetricsByIntegration];
                    });

                    const requiredCapability = requiredCapabilityForMonitoringMetricCategory(
                        metricCategory as AppMonitoringHealthMetricCategory,
                    );
                    const supportedIntegrations = allSupportedIntegrationTypesForCapability(
                        requiredCapability,
                        isRunwayOrg,
                        props.orgId,
                    );
                    const integrationIds = integrations
                        .filter((integration) => supportedIntegrations.includes(integration.id))
                        .map((integration) => integration.id);

                    const hasRequiredCapability = connectedIntegrationCapabilities.includes(requiredCapability);

                    return (
                        <AppSettingsSection
                            key={index}
                            title={sectionTitleForIntegration(sectionTitle, integrationIds)}
                            actionsView={
                                <PlanRestrictedFeatureWrapper
                                    feature={SubscriptionFeatureType.Rollouts}
                                    requiresTooltip
                                >
                                    {(isDisabled) => (
                                        <PermissionedActionButton
                                            icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                                            onClick={() => {
                                                setSelectedMetric(null);
                                                setMetricCategory(metricCategory);
                                            }}
                                            disabled={isDisabled || !hasRequiredCapability}
                                            style={{ display: 'flex', alignItems: 'center' }}
                                            actionType={RunwayUserAction.UAUpdateAppSettings}
                                        >
                                            Add health metric
                                        </PermissionedActionButton>
                                    )}
                                </PlanRestrictedFeatureWrapper>
                            }
                        >
                            {!hasRequiredCapability && (
                                <ConnectIntegrationButton
                                    {...props}
                                    appStoreType={appStoreType}
                                    integrationCapability={requiredCapability}
                                />
                            )}
                            <PlanRestrictedFeatureWrapper
                                feature={SubscriptionFeatureType.Rollouts}
                                shouldFakeDisabledState
                            >
                                <Table
                                    dataSource={sortedMetrics}
                                    columns={columnsForMetricCategory(metricCategory)}
                                    rowKey="id"
                                    pagination={{ hideOnSinglePage: true, defaultPageSize: 5 }}
                                    style={{
                                        marginTop: 24,
                                        marginBottom: 24,
                                        opacity: hasRequiredCapability ? 1 : 0.5,
                                        pointerEvents: hasRequiredCapability ? 'auto' : 'none',
                                    }}
                                />
                            </PlanRestrictedFeatureWrapper>
                        </AppSettingsSection>
                    );
                })}
                <AddEditMonitoringMetricModal
                    availableMetricTypes={availableMetricTypes}
                    metricCategory={metricCategory}
                    metric={selectedMetric}
                    open={!!metricCategory}
                    onCancel={() => {
                        setSelectedMetric(null);
                        setMetricCategory(null);
                    }}
                    appId={props.appId}
                    orgId={props.orgId}
                    currentReleaseStatsMap={currentReleaseStatsMap}
                />
                <ConfirmRemoveMonitoringMetricModal
                    autoHaltAutomation={autoHaltAutomation}
                    autoPromoteAutomation={autoPromoteAutomation}
                    appId={props.appId}
                    orgId={props.orgId}
                    metric={selectedRemoveMetric}
                    onRemoved={() => {
                        setSelectedRemoveMetric(null);
                    }}
                    onCancel={() => {
                        setSelectedRemoveMetric(null);
                    }}
                />
            </Space>
        </div>
    );
};

export default Monitoring;
